/**
 * Realtime frame diagnostics.
 *
 * Every point in the inbound WebSocket pipeline that drops a frame reports through here, so a
 * frame the client cannot handle is always visible in the console together with its payload,
 * never swallowed silently.
 *
 * `console.error` is deliberate (D-19): a dropped frame means the backend said something the
 * client did not understand, which is always worth surfacing while debugging a live session.
 */

const UNHANDLED_PREFIX = '[realtime:unhandled]'

/** Keeps a pathological payload from flooding the console. */
const MAX_RAW_LENGTH = 4000

const truncate = (value: string): string =>
  value.length > MAX_RAW_LENGTH ? `${value.slice(0, MAX_RAW_LENGTH)}… (truncated)` : value

/**
 * Best-effort copy-pasteable rendering of a payload.
 * The console collapses objects to "Object", so a flat string next to it saves a click and
 * survives being copied out of the devtools.
 */
const toRawText = (payload: unknown): string => {
  if (typeof payload === 'string') return truncate(payload)
  if (payload === undefined) return 'undefined'
  try {
    const serialized = JSON.stringify(payload)
    return serialized === undefined ? String(payload) : truncate(serialized)
  } catch {
    return '<unserializable payload>'
  }
}

/**
 * Report an inbound realtime frame that could not be handled.
 *
 * @param source - Where the frame was dropped, e.g. `useMaxRealtime.stream_start`
 * @param reason - Why it was dropped, in plain words
 * @param payload - The frame exactly as received
 */
export function logUnhandledRealtimeFrame(source: string, reason: string, payload: unknown): void {
  console.error(`${UNHANDLED_PREFIX} ${source} — ${reason}`, {
    source,
    reason,
    payload,
    raw: toRawText(payload),
  })
}
